import { DATE_FORMAT_SHORT, formatDate } from '@findy-code/utils/date';
import { useMemo } from 'react';

import type { FetchJobDescriptionDetailFragment } from '../../graphql';

type Props = {
  jobDescription: FetchJobDescriptionDetailFragment;
};
/**
 * 次のidに該当する場合は、マッチ済みの判定にし、タグの背景色をグレーから白に変更する
 * 1:一部リモート可 2:フルリモート可 999:フレックス勤務可能 9:副業可能
 * ref: https://github.com/Findy/Excalibur/blob/develop/db/fixtures/masters/job_descriptions_characteristics.rb
 */
const characteristic = {
  PARTIALLY_REMOTE: 1,
  FULLY_REMOTE: 2,
  FLEXIBLE_WORKING: 999, // TODO: BEの実装に合わせてidを変更する
  SIDE_JOB: 9,
};

const matchedCharacteristicIds = [
  characteristic.PARTIALLY_REMOTE,
  characteristic.FULLY_REMOTE,
  characteristic.FLEXIBLE_WORKING,
  characteristic.SIDE_JOB,
];

// タグの表示順
const characteristicTagOrder = {
  [characteristic.FULLY_REMOTE]: 0, // フルリモート可 (1番目)
  [characteristic.PARTIALLY_REMOTE]: 1, // 一部リモート可 (2番目)
  [characteristic.FLEXIBLE_WORKING]: 2, // フレックス勤務可能 (3番目)
  [characteristic.SIDE_JOB]: 3, // 副業可能 (4番目)
} as const;

const LOWEST_PRIORITY = 999;

export const useCompanyJobDescriptionDetailPresenter = ({
  jobDescription,
}: Props) => {
  const jobDescriptionsCharacteristicsWithMatches = useMemo(() => {
    const updatedCharacteristics =
      jobDescription.jobDescriptionsCharacteristics.map((characteristic) => {
        if (matchedCharacteristicIds.includes(characteristic.id)) {
          return {
            ...characteristic,
            isMatched: true,
          };
        }
        return characteristic;
      });

    updatedCharacteristics.sort((a, b) => {
      // idが存在する場合は定義された順序で、存在しない場合は最後に表示
      const orderA =
        characteristicTagOrder[a.id as keyof typeof characteristicTagOrder] ??
        LOWEST_PRIORITY;
      const orderB =
        characteristicTagOrder[b.id as keyof typeof characteristicTagOrder] ??
        LOWEST_PRIORITY;
      return orderA - orderB;
    });

    return updatedCharacteristics;
  }, [jobDescription]);

  const companyAiUsageLatestUpdatedAt = useMemo(() => {
    const latestUpdatedAt =
      jobDescription.company.companyAiUsage?.latestUpdatedAt;
    if (!latestUpdatedAt) return undefined;
    return `最終更新日:${formatDate(latestUpdatedAt, DATE_FORMAT_SHORT)}`;
  }, [jobDescription.company.companyAiUsage]);

  return {
    jobDescriptionsCharacteristicsWithMatches,
    companyAiUsageLatestUpdatedAt,
  } as const;
};
